import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';
import { PASSWORD_RULES } from '@/features/auth/schemas/password.schema';
import type { StaffDetails, StaffMember } from '@/types/staff';

/**
 * What a staff account must look like before it can be saved.
 *
 * Mirrors `backend/src/modules/institute/staff/staff.validation.ts`. These rules
 * are for fast feedback only — the server checks every one of them again and
 * stays the authority (CLAUDE.md §6).
 *
 * The password is optional. Typed, it must pass the product's one strength
 * rule and the account changes it at first sign-in. Left blank on create, the
 * person is emailed a link to set their own; left blank on an edit, their
 * password is not touched. On an edit it travels on its own route
 * (`staffApi.setPassword`), never in the update body.
 */

/** The account types an institute may create. `ADMIN` is support's to grant. */
export const STAFF_TYPES = [
  'PRINCIPAL',
  'TEACHER',
  'ACCOUNTANT',
  'LIBRARIAN',
  'COUNSELLOR',
  'DRIVER',
] as const;

/** Indian names carry initials, apostrophes and hyphens: "D'Souza", "Jean-Paul". */
const NAME_PATTERN = /^[\p{L} .'-]+$/u;
const NAME_MESSAGE = "Letters, spaces and . ' - only";
/** Indian mobile numbers are ten digits and never begin below 6. */
const MOBILE_PATTERN = /^[6-9]\d{9}$/;

const name = (label: string) =>
  z
    .string()
    .trim()
    .min(2, `${label} is too short`)
    .max(80, `${label} is too long`)
    .regex(NAME_PATTERN, NAME_MESSAGE);

/** Blank inputs leave as `undefined` rather than `''`, as the API expects. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'That is too long')
    .optional()
    .transform((value) => value || undefined);

/** Upper-cased and unspaced before the check, as the server does. Blank leaves as `undefined`. */
const optionalCode = (pattern: RegExp, message: string) =>
  z
    .string()
    .trim()
    .transform((value) => value.replace(/\s/g, '').toUpperCase())
    .pipe(z.string().regex(pattern, message).or(z.literal('')))
    .optional()
    .transform((value) => value || undefined);

const optionalMobile = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(MOBILE_PATTERN, 'Enter a valid 10-digit mobile number').or(z.literal('')))
  .optional()
  .transform((value) => value || undefined);

const optionalName = (label: string) =>
  name(label)
    .or(z.literal(''))
    .optional()
    .transform((value) => value || undefined);

const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
  .or(z.literal(''))
  .optional()
  .transform((value) => value || undefined);

/** A choice from a `Select`, whose "nothing chosen" is `''`. */
const optionalChoice = <T extends [string, ...string[]]>(values: T) =>
  z
    .enum(values)
    .or(z.literal(''))
    .optional()
    .transform((value) => value || undefined);

/** A number typed into a text box. Blank is `undefined`; anything else must be a number. */
const optionalNumber = (rules: z.ZodNumber) =>
  z
    .string()
    .trim()
    .optional()
    .transform((value) => (value ? Number(value) : undefined))
    .pipe(rules.optional());

/**
 * The profile's personal, identity, contact, bank, employment and background
 * details. Mirrors `staffDetailsSchema` in the server's `staff.validation.ts`.
 *
 * Sent whole by the form and never by anything else — a status change from the
 * list leaves it out, and the server then leaves every detail as it is.
 */
export const staffDetailsSchema = z.object({
  dateOfBirth: optionalDate.refine(
    (value) => !value || value <= new Date().toISOString().slice(0, 10),
    'The date of birth cannot be in the future',
  ),
  gender: optionalChoice(['MALE', 'FEMALE', 'OTHER']),
  bloodGroup: optionalChoice(['UNKNOWN', 'A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']),
  maritalStatus: optionalChoice(['SINGLE', 'MARRIED', 'SEPARATED', 'DIVORCED', 'WIDOWED']),
  nationality: optionalText(40),

  aadhaarNumber: optionalCode(/^[2-9]\d{11}$/, 'Aadhaar is 12 digits'),
  panNumber: optionalCode(/^[A-Z]{5}\d{4}[A-Z]$/, 'A PAN looks like ABCDE1234F'),
  uanNumber: optionalCode(/^\d{12}$/, 'A UAN is 12 digits'),
  pfNumber: optionalCode(/^[A-Z0-9/]{5,26}$/, 'Letters, numbers and / only'),
  esiNumber: optionalCode(/^\d{10}(\d{7})?$/, 'An ESI number is 10 or 17 digits'),
  passportNumber: optionalCode(/^[A-Z0-9]{6,12}$/, 'Letters and numbers only, 6 to 12'),

  alternateMobile: optionalMobile,
  emergencyContactName: optionalName('The contact name'),
  emergencyContactMobile: optionalMobile,
  presentAddress: optionalText(250),
  permanentAddress: optionalText(250),

  bankName: optionalText(100),
  bankAccountNumber: optionalCode(/^\d{9,18}$/, 'An account number is 9 to 18 digits'),
  bankIfsc: optionalCode(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'An IFSC looks like SBIN0001234'),
  bankAccountHolder: optionalName('The account holder'),
  /** Cash, or into the account above — which bank transfer then requires. */
  salaryPaymentMode: optionalChoice(['CASH', 'BANK_TRANSFER']),

  employmentType: optionalChoice(['FULL_TIME', 'PART_TIME', 'CONTRACT', 'VISITING']),
  confirmedOn: optionalDate,
  probationEndsOn: optionalDate,
  noticePeriodDays: optionalNumber(
    z
      .number({ invalid_type_error: 'Enter a number of days' })
      .int('Whole days only')
      .min(0, 'That cannot be negative')
      .max(365, 'At most 365 days'),
  ),
  workLocation: optionalText(80),
  biometricId: optionalCode(/^[A-Z0-9_-]{1,30}$/, 'Letters, numbers, - and _ only'),
  reportingToUuid: z
    .string()
    .optional()
    .transform((value) => value || undefined),

  qualification: optionalText(150),
  languagesKnown: optionalText(150),
  experienceYears: optionalNumber(
    z
      .number({ invalid_type_error: 'Enter a number of years' })
      .min(0, 'That cannot be negative')
      .max(60, 'At most 60 years')
      .multipleOf(0.1, 'One decimal place at most'),
  ),
  skills: optionalText(255),
});

export const staffSchema = z.object({
  firstName: name('The first name'),
  lastName: name('The last name'),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'An email address is required')
    .email('Enter a valid email address')
    .max(150, 'That address is too long'),
  /** Spaces and hyphens are how people write a number down; strip, then check. */
  mobile: z
    .string()
    .trim()
    .transform((value) => value.replace(/[\s-]/g, ''))
    .pipe(z.string().regex(MOBILE_PATTERN, 'Enter a valid 10-digit mobile number').or(z.literal('')))
    .transform((value) => value || undefined),
  staffType: z.enum(STAFF_TYPES, {
    errorMap: () => ({ message: 'Choose what kind of account this is' }),
  }),
  /** Required while the account is active — see the refinement below. */
  roleUuid: z
    .string()
    .optional()
    .transform((value) => value || undefined),
  // No employee code: the server issues it on create (`EMP/2026/0001`) and it
  // is never edited, so the form only displays it.
  designation: optionalText(80),
  joinedOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
    .or(z.literal(''))
    .optional()
    .transform((value) => value || undefined),
  /** Only meaningful when editing; the create form never sends it. */
  status: z.enum(['ACTIVE', 'INACTIVE'], {
    errorMap: () => ({ message: 'Choose whether this account is active' }),
  }),
  /** Create only, and optional — blank means "email them a link instead". */
  password: z
    .string()
    .max(128, 'That is too long')
    .optional()
    .transform((value) => value || undefined),
  confirmPassword: z.string().optional(),
  /** Left out entirely by a status change, so the server keeps every detail. */
  details: staffDetailsSchema.optional(),
})
  .superRefine((values, ctx) => {
    // Every rule reported, from the same list the checklist under the field
    // reads, so a tick there can never sit beside a rule this still refuses.
    if (values.password) {
      for (const rule of PASSWORD_RULES) {
        if (!rule.test(values.password)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: rule.message });
        }
      }
    }
    if ((values.password ?? '') !== (values.confirmPassword ?? '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
        message: values.confirmPassword ? 'Both passwords must match' : 'Type the password again',
      });
    }
  })
  /** A driver signs in to the mobile app by mobile number, so it is required. */
  .superRefine((values, ctx) => {
    if (values.staffType === 'DRIVER' && !values.mobile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mobile'],
        message: 'A driver signs in with their mobile number, so it is required',
      });
    }
  })
  /**
   * Every permission comes from the role, so an active account without one
   * signs in to a dashboard and nothing else. Mirrors the server's
   * `requireRoleWhenActive`; an inactive account may still be saved without one.
   */
  .superRefine((values, ctx) => {
    if (values.status === 'ACTIVE' && !values.roleUuid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['roleUuid'],
        message: 'Choose a role — without one they can open nothing but the dashboard',
      });
    }
  })
  /** Paying by transfer needs an account to pay into. Mirrors `requireBankForTransfer`. */
  .superRefine((values, ctx) => {
    if (values.details?.salaryPaymentMode !== 'BANK_TRANSFER') return;
    for (const [field, message] of BANK_FIELDS) {
      if (!values.details[field]) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['details', field], message });
      }
    }
  });

const BANK_FIELDS = [
  ['bankName', 'Enter the bank name to pay by transfer'],
  ['bankAccountNumber', 'Enter the account number to pay by transfer'],
  ['bankIfsc', 'Enter the IFSC to pay by transfer'],
  ['bankAccountHolder', 'Enter the account holder to pay by transfer'],
] as const;

/** What the form holds — every field a string, as the inputs give them to us. */
export type StaffFormValues = z.input<typeof staffSchema>;
/**
 * What an edit sends on: blanks dropped. The password fields never travel with
 * an edit — the server refuses them there.
 */
export type StaffPayload = Omit<z.output<typeof staffSchema>, 'password' | 'confirmPassword'>;

/**
 * `raw` keeps the form state holding what the inputs hold. Without it the
 * submit handler is handed the *transformed* values while the form still holds
 * strings, so the declared `StaffFormValues` would be a lie. Same reasoning as
 * the class and admission forms.
 */
export const staffFormResolver: Resolver<StaffFormValues> = zodResolver(staffSchema, undefined, {
  raw: true,
});

/**
 * Opening an account asks for more than correcting one: phone, role and date
 * of joining, which the form marks with a star. Mirrors the server's
 * `requireOnCreate`, and like it applies to a new account only — an older
 * record without them can still be edited.
 */
const createStaffSchema = staffSchema.superRefine((values, ctx) => {
  // No role here: a new account is always active, so the base schema already
  // demands one.
  const missing: [keyof typeof values, string][] = [
    ['mobile', 'A phone number is required'],
    ['joinedOn', 'Choose the date of joining'],
    ['designation', 'Enter the designation'],
  ];
  for (const [field, message] of missing) {
    if (!values[field]) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
  }
  // The enrolment record. Mirrors the server's `requireEnrolmentDetails`.
  for (const [field, message] of ENROLMENT_DETAILS) {
    if (!values.details?.[field]) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['details', field], message });
    }
  }
});

/** The details a new account must carry, which the form marks with a star. */
export const ENROLMENT_DETAILS = [
  ['gender', 'Choose a gender'],
  ['dateOfBirth', 'Enter the date of birth'],
  ['employmentType', 'Choose the employment type'],
  ['presentAddress', 'Enter the present address'],
  ['emergencyContactName', 'Enter an emergency contact'],
  ['emergencyContactMobile', "Enter the emergency contact's mobile"],
  ['panNumber', 'Enter the PAN'],
  ['salaryPaymentMode', 'Choose how the salary is paid'],
] as const;

export const createStaffFormResolver: Resolver<StaffFormValues> = zodResolver(
  createStaffSchema,
  undefined,
  { raw: true },
);

export const EMPTY_STAFF_FORM: StaffFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  staffType: 'TEACHER',
  roleUuid: '',
  designation: '',
  joinedOn: '',
  status: 'ACTIVE',
  password: '',
  confirmPassword: '',
  details: toStaffDetailsFormValues(null),
};

/**
 * An account as the form holds it, for correcting it.
 *
 * Shared by the edit form and the profile's status action: the server takes the
 * whole record on every update, so changing only the status still has to send
 * every other field exactly as it stands. A `SUSPENDED` account reads as
 * `INACTIVE` — the lockout's status is not one a person may choose.
 */
export function toStaffFormValues(member: StaffMember): StaffFormValues {
  return {
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
    mobile: member.mobile ?? '',
    staffType: member.staffType as StaffFormValues['staffType'],
    roleUuid: member.role?.uuid ?? '',
    designation: member.designation ?? '',
    joinedOn: member.joinedOn ?? '',
    status: member.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE',
  };
}

export type StaffDetailsFormValues = z.input<typeof staffDetailsSchema>;

/**
 * A profile's details as the form holds them — every field a string.
 *
 * Kept apart from `toStaffFormValues` on purpose: that one also feeds the
 * status action, which must send the account alone. Folding the details in
 * there would have every suspension rewrite the person's bank details.
 */
export function toStaffDetailsFormValues(details: StaffDetails | null | undefined): StaffDetailsFormValues {
  const text = (value: string | null | undefined) => value ?? '';
  const number = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));

  return {
    dateOfBirth: text(details?.dateOfBirth),
    gender: text(details?.gender) as StaffDetailsFormValues['gender'],
    bloodGroup: text(details?.bloodGroup) as StaffDetailsFormValues['bloodGroup'],
    maritalStatus: text(details?.maritalStatus) as StaffDetailsFormValues['maritalStatus'],
    nationality: text(details?.nationality),
    aadhaarNumber: text(details?.aadhaarNumber),
    panNumber: text(details?.panNumber),
    uanNumber: text(details?.uanNumber),
    pfNumber: text(details?.pfNumber),
    esiNumber: text(details?.esiNumber),
    passportNumber: text(details?.passportNumber),
    alternateMobile: text(details?.alternateMobile),
    emergencyContactName: text(details?.emergencyContactName),
    emergencyContactMobile: text(details?.emergencyContactMobile),
    presentAddress: text(details?.presentAddress),
    permanentAddress: text(details?.permanentAddress),
    bankName: text(details?.bankName),
    bankAccountNumber: text(details?.bankAccountNumber),
    bankIfsc: text(details?.bankIfsc),
    bankAccountHolder: text(details?.bankAccountHolder),
    salaryPaymentMode: text(details?.salaryPaymentMode) as StaffDetailsFormValues['salaryPaymentMode'],
    employmentType: text(details?.employmentType) as StaffDetailsFormValues['employmentType'],
    confirmedOn: text(details?.confirmedOn),
    probationEndsOn: text(details?.probationEndsOn),
    noticePeriodDays: number(details?.noticePeriodDays),
    workLocation: text(details?.workLocation),
    biometricId: text(details?.biometricId),
    reportingToUuid: text(details?.reportingTo?.uuid),
    qualification: text(details?.qualification),
    languagesKnown: text(details?.languagesKnown),
    experienceYears: number(details?.experienceYears),
    skills: text(details?.skills),
  };
}
